import { useState } from 'react';
import type { IconType } from 'react-icons';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { MdContentCopy, MdDelete, MdEdit, MdLinkOff, MdLock, MdPeople } from 'react-icons/md';
import type { AppCatalogEntry, AppVisibility } from '@api/apps';
import type { UserAppSummary } from '@api/appBuilder';
import { axiosErrorMessage } from '@api/errors';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import type { AppAction } from './AppActionsMenu';
import { isOrgAdmin, myAppActions, sharedAppActions, type GalleryActionId } from './galleryActions';
import { useDuplicateAndOpen } from '../useDuplicateAndOpen';
import { useAppBuilderActions } from './useAppBuilder';
import { builderPath } from './userAppStatus';

type Handlers = Partial<Record<GalleryActionId, () => void>>;

const ICONS: Record<GalleryActionId, IconType> = {
  edit: MdEdit,
  duplicate: MdContentCopy,
  share: MdPeople,
  unshare: MdLock,
  turnOffLink: MdLinkOff,
  delete: MdDelete,
};

export const useGalleryActions = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showError } = useSnackbar();
  const { user } = useAuth();
  const actions = useAppBuilderActions();
  const { duplicateAndOpen, isDuplicating } = useDuplicateAndOpen();
  const [pendingDelete, setPendingDelete] = useState<UserAppSummary | null>(null);
  const orgAdmin = isOrgAdmin(user?.orgRole);

  const failed = (err: unknown) =>
    showError(
      axiosErrorMessage(
        err,
        intl.formatMessage({
          id: 'apps.builder.error.action',
          defaultMessage: 'That did not work. Reload the page and try again.',
        }),
      ),
    );

  const labels: Record<GalleryActionId, string> = {
    edit: intl.formatMessage({ id: 'apps.builder.action.edit', defaultMessage: 'Edit' }),
    duplicate: intl.formatMessage({ id: 'apps.builder.action.duplicate', defaultMessage: 'Duplicate' }),
    share: intl.formatMessage({ id: 'apps.builder.action.share', defaultMessage: 'Share with organization' }),
    unshare: intl.formatMessage({ id: 'apps.builder.action.unshare', defaultMessage: 'Make private' }),
    turnOffLink: intl.formatMessage({ id: 'apps.builder.action.turnOffLink', defaultMessage: 'Turn off public link' }),
    delete: intl.formatMessage({ id: 'apps.builder.action.delete', defaultMessage: 'Delete' }),
  };

  const toActions = (ids: readonly GalleryActionId[], handlers: Handlers): AppAction[] =>
    ids.flatMap((id) => {
      const onSelect = handlers[id];
      const Icon = ICONS[id];
      return onSelect
        ? [
            {
              id,
              label: labels[id],
              icon: <Icon size={16} />,
              onSelect,
              danger: id === 'delete',
              disabled: id === 'duplicate' && isDuplicating,
            },
          ]
        : [];
    });

  const openBuilder = (appKey: string) => navigate(builderPath(appKey));

  const setVisibility = (appKey: string, visibility: AppVisibility) => {
    actions.setVisibility({ appKey, visibility }).catch(failed);
  };

  const turnOffLink = (appKey: string) => {
    actions.revokePublicLink(appKey).catch(failed);
  };

  const forMine = (app: UserAppSummary): AppAction[] =>
    toActions(myAppActions(app), {
      edit: () => openBuilder(app.appKey),
      duplicate: () => duplicateAndOpen(app.appKey),
      share: () => setVisibility(app.appKey, 'ORG'),
      unshare: () => setVisibility(app.appKey, 'PRIVATE'),
      turnOffLink: () => turnOffLink(app.appKey),
      delete: () => setPendingDelete(app),
    });

  const forShared = (entry: AppCatalogEntry): AppAction[] =>
    toActions(sharedAppActions(entry, orgAdmin), {
      edit: () => openBuilder(entry.appKey),
      duplicate: () => duplicateAndOpen(entry.appKey),
      unshare: () => {
        actions.unshare(entry.appKey).catch(failed);
      },
      turnOffLink: () => turnOffLink(entry.appKey),
    });

  const confirmDelete = () =>
    pendingDelete
      ? actions.remove(pendingDelete.appKey).catch((err: unknown) => {
          failed(err);
          throw err;
        })
      : undefined;

  return {
    forMine,
    forShared,
    pendingDelete,
    confirmDelete,
    cancelDelete: () => setPendingDelete(null),
  };
};
